import { AxiosResponse } from "axios";
import classNames from "classnames";
import { DotsCheckbox } from "dots-ui/dotsCheckbox";
import {
  DataTableColumnType,
  IColumnConfig,
  IDataTableRowValue,
  ITableColumnProps,
  TableColumnTemplate,
} from "dots-ui/dotsTable";
import parse from "html-react-parser";
import _ from "lodash";
import { Link } from "react-router-dom";
import { AvisFileDownload } from "../components/shared/components/form";
import { AvisConstants } from "../data/avisConstants";
import { ILinkValue, ResCallback } from "../models";
import { IBoolLocalization } from "../models/IBoolLocalization";
import { TimeUtils } from "./";

export class TableUtils {
  static convertWidth(width?: number | string): string | undefined {
    if (!width) return undefined;

    return typeof width === "number" ? `${width}px` : width;
  }

  static columnConfig<T>(
    name: keyof T | string,
    title: string,
    columnType: DataTableColumnType,
    template?: TableColumnTemplate,
    minWidth?: number | string,
  ): IColumnConfig {
    const defaultMetadata =
      columnType === "DateTime"
        ? AvisConstants.table_datetime_column_metadata
        : AvisConstants.table_default_column_metadata;

    return {
      metadata: {
        ...defaultMetadata,
        minWidth: this.convertWidth(minWidth),
      },
      type: columnType,
      name: name as string,
      translation: title,
      template: template,
    } as IColumnConfig;
  }

  static columnConfigString<T>(
    name: keyof T,
    title: string,
    template?: TableColumnTemplate,
    minWidth?: number | string,
  ): IColumnConfig {
    return this.columnConfig<T>(name, title, "String", template, minWidth);
  }

  static columnConfigLink<T>(
    name: keyof T,
    title: string,
    getLink: (rowData: T) => ILinkValue | undefined,
    minWidth?: number | string,
  ): IColumnConfig {
    const linkColumnTemplate = (rowData: any): JSX.Element => {
      const linkValue = getLink(rowData as T);
      if (!linkValue) return <div title={rowData[name]}>{rowData[name]}</div>;
      return (
        <div title={linkValue.title}>
          <Link to={linkValue.url} target={linkValue.target}>
            {linkValue.title}
          </Link>
        </div>
      );
    };

    return this.columnConfig<T>(
      name,
      title,
      "String",
      linkColumnTemplate,
      minWidth,
    );
  }
  static columnConfigDownloadInvoicePdfXml<T>(
    name: keyof T,
    title: string,
    callApiOnClickPdf: (rowData: T) => Promise<AxiosResponse<object, any>>,
    callApiOnClickXml: (rowData: T) => Promise<AxiosResponse<object, any>>,
    getNumberValue?: (rowData: T) => string | undefined,
  ): IColumnConfig {
    const downloadColumnTemplate = (rowData: T): JSX.Element => {
      const numberValue = getNumberValue?.(rowData);
      const wrappedPromisePdf = () => {
        return callApiOnClickPdf(rowData);
      };
      const wrappedPromiseXml = () => {
        return callApiOnClickXml(rowData);
      };
      return (
        <div className="avis-table-cell-value">
          {numberValue && <div className="invoice-number">{numberValue}</div>}
          <div className="invoice-download">
            <AvisFileDownload
              title={"PDF"}
              callApiOnClick={wrappedPromisePdf}
              asImage={false}
            />
            <AvisFileDownload
              title={"XML"}
              callApiOnClick={wrappedPromiseXml}
              asImage={false}
            />
          </div>
        </div>
      );
    };

    return this.columnConfig<T>(name, title, "String", downloadColumnTemplate);
  }
  static columnConfigDateTime<T>(
    name: keyof T,
    title: string,
    template?: TableColumnTemplate,
    minWidth?: number | string,
  ): IColumnConfig {
    return this.columnConfig<T>(name, title, "DateTime", template, minWidth);
  }

  static columnConfigMoney<T>(
    name: keyof T,
    title: string,
    getLink?: (rowData: T) => string,
    onClick?: (navigateTo: string) => void,
    minWidth?: number | string,
  ): IColumnConfig {
    const templateMoney = (
      rowData: any,
      column: ITableColumnProps,
    ): JSX.Element => {
      if (!column.field) return <></>;

      const fieldData = parseFloat(rowData[column.field]);
      if (isNaN(fieldData)) return <></>;

      const fieldDataString = fieldData.toFixed(2);

      if (getLink && onClick) {
        const urlValue = getLink(rowData as T);

        return (
          <div className="avis-column-money" title={fieldDataString}>
            <Link to={urlValue}>{fieldDataString}</Link>
          </div>
        );
      }

      return (
        <div className="avis-column-money" title={fieldDataString}>
          {fieldDataString}
        </div>
      );
    };

    return this.columnConfig<T>(
      name,
      title,
      "Decimal",
      templateMoney,
      minWidth,
    );
  }

  static columnConfigBoolean<T>(
    name: keyof T,
    title: string,
    res: ResCallback,
    minWidth?: number | string,
    localization?: IBoolLocalization,
  ): IColumnConfig {
    const renderBoolTemplate = (
      rowData: IDataTableRowValue,
      column: ITableColumnProps,
    ): JSX.Element => {
      if (!column.field) return <div></div>;

      const defaultLocalization: IBoolLocalization = {
        trueValue: res("table_BooleanYes"),
        falseValue: res("table_BooleanNo"),
      };

      const boolValue = (rowData as any)[column.field];
      const boolLocalizationO = localization ?? defaultLocalization;

      const resultTooltip = boolValue
        ? defaultLocalization.trueValue?.toString()
        : defaultLocalization.falseValue?.toString();

      const result = boolValue
        ? boolLocalizationO.trueValue
        : boolLocalizationO.falseValue;

      return <div title={resultTooltip}>{result}</div>;
    };
    return this.columnConfig<T>(
      name,
      title,
      "Boolean",
      renderBoolTemplate,
      minWidth,
    );
  }

  static columnConfigPeriod<T>(
    dateFromField: keyof T,
    dateToField: keyof T,
    title: string,
    getLink?: (rowData: T) => string | undefined,
    minWidth?: number | string,
  ): IColumnConfig {
    const periodColumnTemplate = (
      rowData: any,
      column: ITableColumnProps,
    ): JSX.Element => {
      if (!column.field) return <></>;

      const periodValue = TimeUtils.formatPeriod(
        rowData[dateFromField],
        rowData[dateToField],
      );

      if (getLink) {
        const urlValue = getLink(rowData as T);

        return (
          <div className="avis-table-cell-value-link" title={periodValue}>
            {urlValue ? <Link to={urlValue}>{periodValue}</Link> : periodValue}
          </div>
        );
      }

      return (
        <div className="avis-table-cell-value" title={periodValue}>
          {periodValue}
        </div>
      );
    };
    return this.columnConfig<T>(
      dateFromField,
      title,
      "String",
      periodColumnTemplate,
      minWidth,
    );
  }
  static columnConfigNested<T>(
    name: keyof T,
    nestedValueProp: string,
    title: string,
    minWidth?: number | string,
    transform?: (val: any) => any,
    className?: string,
  ): IColumnConfig {
    const templateNestedObjectValue = (
      rowData: any,
      column: ITableColumnProps,
    ): JSX.Element => {
      if (!column.field) return <></>;

      let fieldData = _.get(rowData, column.field, undefined);

      if (!fieldData) return <></>;
      if (transform) fieldData = transform(fieldData);

      const classNameValue = classNames("avis-table-cell-value", className);
      return (
        <div className={classNameValue} title={fieldData}>
          {fieldData}
        </div>
      );
    };

    return this.columnConfig<T>(
      `${name as string}.${nestedValueProp}`,
      title,
      "Object",
      templateNestedObjectValue,
      minWidth,
    );
  }

  static columnConfigCheckbox<T>(
    name: keyof T,
    title: string,
    selectedReadOnly: number[],
    selectedKeys: number[],
    onChange: (rowData: T, checked: boolean) => void,
  ): IColumnConfig {
    const checkboxColumnTemplate = (rowData: T): JSX.Element => {
      const disabled =
        selectedReadOnly.filter((item) => item === (rowData[name] as number))
          .length > 0;
      const checked =
        selectedKeys.filter((item) => item === (rowData[name] as number))
          .length > 0;

      return (
        <DotsCheckbox
          onChange={(_e, val) => {
            onChange(rowData, val);
          }}
          checked={checked}
          disabled={disabled}
        ></DotsCheckbox>
      );
    };

    return this.columnConfig<T>(
      name,
      title,
      "String",
      checkboxColumnTemplate,
      60,
    );
  }
  static columnConfigStringHTMLEnabled<T>(
    name: keyof T,
    title: string,
    postProcess?: (
      rowData: any,
      value: string | JSX.Element | JSX.Element[],
    ) => string | JSX.Element | JSX.Element[],
  ): IColumnConfig {
    const htmlEnabledColumnTemplate = (rowData: any): JSX.Element => {
      const rowValue = rowData?.[name] ?? "";
      let parsedValue = parse(rowValue);
      if (postProcess) parsedValue = postProcess(rowData, parsedValue);

      return <div className="avis-formatted-value">{parsedValue}</div>;
    };
    return this.columnConfig<T>(
      name,
      title,
      "String",
      htmlEnabledColumnTemplate,
    );
  }
}
